import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaBuilding, FaChartLine, FaEllipsisV, FaRegClock, FaSignInAlt, FaSignOutAlt, FaTags } from 'react-icons/fa';
import { Dropdown, StatusBadge } from '@/components/UI';
import type { TradingStrategyFormData } from '../tradingStrategyEditorSubmitPayloads';
import { getCoverageTone } from './helpers';

interface StrategyIdentityHeaderProps {
    canEditStrategy: boolean;
    coveragePercent: number | null;
    attachedCandidateCount: number | null;
    isDeletePending: boolean;
    isEditMode: boolean;
    isOwnedStrategy: boolean;
    formData: TradingStrategyFormData;
    liveEntryAttachmentVersion: number | null;
    liveExitAttachmentVersion: number | null;
    strategyId: number | null;
    onDelete: () => void;
    onShare: () => void;
    onToggleActive: () => void;
}

const entityTypeLabel = (type: string): string => {
    if (type === 'Asset') return 'Stock';
    if (type === 'Cryptocurrency') return 'Crypto';
    if (type === 'AssetIndex') return 'Index research';

    return type;
};

const MetaChip = ({ icon, label, className = 'bg-zinc-800 text-zinc-300' }: { icon: ReactNode; label: string; className?: string }) => (
    <span className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs tabular-nums ${className}`}>
        <span aria-hidden="true" className="text-[11px]">
            {icon}
        </span>
        {label}
    </span>
);

const MenuItem = ({ label, danger = false, onClick }: { label: string; danger?: boolean; onClick: () => void }) => (
    <button
        className={`block w-full px-4 py-2 text-left text-sm hover:bg-zinc-800 ${danger ? 'text-red-400' : 'text-zinc-300'}`}
        role="menuitem"
        type="button"
        onClick={onClick}
    >
        {label}
    </button>
);

const StatusPill = ({ active, canToggle, onToggle }: { active: boolean; canToggle: boolean; onToggle: () => void }) => {
    const badge = <StatusBadge status={active ? 'active' : 'inactive'} />;

    if (!canToggle) return badge;

    return (
        <button
            aria-label={active ? 'Set strategy inactive' : 'Set strategy active'}
            className="rounded transition-opacity hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-p314-blue"
            title="Toggle active state (staged until you save)"
            type="button"
            onClick={onToggle}
        >
            {badge}
        </button>
    );
};

const HeaderOverflowMenu = ({
    canManageStrategy,
    isDeletePending,
    strategyId,
    onShare,
    onDelete
}: {
    canManageStrategy: boolean;
    isDeletePending: boolean;
    strategyId: number | null;
    onShare: () => void;
    onDelete: () => void;
}) => {
    const navigate = useNavigate();

    return (
        <Dropdown
            hideChevron
            buttonClassName="px-2 border border-zinc-700"
            buttonContent={<FaEllipsisV aria-label="More actions" />}
            position="right"
        >
            {canManageStrategy && (
                <>
                    <MenuItem label="Open dashboard" onClick={() => navigate(`/user/strategyDashboard/${strategyId}`)} />
                    <MenuItem label="Entry Optimizer" onClick={() => navigate(`/user/entryOptimizer/new?strategyId=${strategyId}`)} />
                    <MenuItem label="AERO" onClick={() => navigate(`/user/aero/new?strategyId=${strategyId}`)} />
                    <MenuItem label="Share" onClick={onShare} />
                    <MenuItem label="Duplicate" onClick={() => navigate(`/user/tradingStrategy/new?copyFrom=${strategyId}`)} />
                </>
            )}
            <MenuItem label="Back to strategies" onClick={() => navigate('/user/tradingStrategies')} />
            {canManageStrategy && <MenuItem danger label={isDeletePending ? 'Deleting...' : 'Delete'} onClick={onDelete} />}
        </Dropdown>
    );
};

/**
 * Identity bar for the strategy editor. Carries the name, status and the live state of every major
 * attachment. It deliberately holds no save action — the save bar at the bottom of the page is the
 * page's only commit point.
 */
const StrategyIdentityHeader = ({
    canEditStrategy,
    coveragePercent,
    attachedCandidateCount,
    isDeletePending,
    isEditMode,
    isOwnedStrategy,
    formData,
    liveEntryAttachmentVersion,
    liveExitAttachmentVersion,
    strategyId,
    onDelete,
    onShare,
    onToggleActive
}: StrategyIdentityHeaderProps) => {
    const displayName = formData.name.trim() || (isEditMode ? 'Untitled strategy' : 'New trading strategy');
    const entityLabels = formData.allowedUnderlyingEntityTypes.map(entityTypeLabel);
    const canManageStrategy = isEditMode && strategyId !== null && isOwnedStrategy;

    return (
        <div className="sticky top-0 z-20 mt-4 mb-4 rounded-lg border border-zinc-800 bg-[#1a1a1a]/95 px-4 py-3 backdrop-blur">
            <div className="flex flex-wrap items-center gap-3">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <h3 className="truncate text-xl font-semibold text-white">{displayName}</h3>
                        {isEditMode && strategyId !== null && <span className="shrink-0 text-sm text-zinc-500">#{strategyId}</span>}
                        {isEditMode && (
                            <StatusPill
                                active={formData.active}
                                canToggle={
                                    canEditStrategy &&
                                    isOwnedStrategy &&
                                    !formData.allowedUnderlyingEntityTypes.includes('AssetIndex') &&
                                    !formData.allowedUnderlyingEntityTypes.includes('FuturesProduct')
                                }
                                onToggle={onToggleActive}
                            />
                        )}
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {entityLabels.map((label) => (
                            <MetaChip icon={<FaBuilding />} key={label} label={label} />
                        ))}
                        <MetaChip icon={<FaRegClock />} label={formData.duration} />
                        {coveragePercent !== null && (
                            <MetaChip
                                className={getCoverageTone(coveragePercent).chipClass}
                                icon={<FaChartLine />}
                                label={`${coveragePercent}% coverage`}
                            />
                        )}
                        {liveEntryAttachmentVersion !== null && (
                            <MetaChip icon={<FaSignInAlt />} label={`Entry v${liveEntryAttachmentVersion}`} />
                        )}
                        {liveExitAttachmentVersion !== null && (
                            <MetaChip icon={<FaSignOutAlt />} label={`Exit v${liveExitAttachmentVersion}`} />
                        )}
                        {attachedCandidateCount !== null && attachedCandidateCount > 0 && (
                            <MetaChip
                                icon={<FaTags />}
                                label={`${attachedCandidateCount} ${attachedCandidateCount === 1 ? 'candidate' : 'candidates'}`}
                            />
                        )}
                    </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                    <HeaderOverflowMenu
                        canManageStrategy={canManageStrategy}
                        isDeletePending={isDeletePending}
                        strategyId={strategyId}
                        onDelete={onDelete}
                        onShare={onShare}
                    />
                </div>
            </div>
        </div>
    );
};

export default StrategyIdentityHeader;
